/**
 * When the service worker registers.
 *
 * It registered on window `load`, which in this app fires as soon as the
 * JavaScript has arrived and before the first screen's data has. The
 * precache (the shell, every route and two fonts, a few hundred KB) then
 * downloaded alongside the Firestore reads the person was waiting for, and
 * on a shared cell that pushed the home screen's main content back by about
 * a third of a second.
 *
 * Now a screen asks for it once it has what it came for: the first answer
 * from the server on a screen that reads data (lib/hooks.ts), or the
 * sign-in page once it has painted. Registration then waits for the main
 * thread to go idle. Any screen that never asks still gets it ten seconds
 * after load, so offline support never depends on a page remembering.
 *
 * The registration itself is what vite-plugin-pwa's registerSW.js did.
 * autoUpdate's worker takes over on its own; what the injected script also
 * did, and this did not, was reload the page once the new worker had it,
 * so an open tab or an installed copy kept the build it was opened on
 * (lib/swUpdate.ts says where that is done at once and where it waits).
 */
import { isUpdate, mayReloadNow } from './swUpdate';

let requested = false;
let pendingReload = false;

/** True once, when a newer build took over a page that had to wait for a safe moment to reload. */
export function takePendingReload(): boolean {
  const p = pendingReload;
  pendingReload = false;
  return p;
}

let watching = false;

/**
 * Armed at start-up, not at registration. The browser checks for a new
 * worker on its own as the page is opened, and a small build installs and
 * takes over inside a few seconds, while this app registers ten seconds
 * after load on a page with no data to wait for. Armed at registration,
 * the listener missed the takeover and the old shell stayed.
 */
function watchForNewBuild(): void {
  if (watching) return;
  watching = true;
  // Whether a worker controls this page. A page with none is a first
  // visit, and the new worker claiming it is not an update. From then on
  // the page is controlled, so the next change is: a first-visit tab left
  // open across a deploy used to keep the old build, because this was read
  // once at start-up and stayed false for the life of the page.
  let controlled = Boolean(navigator.serviceWorker.controller);
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    const update = isUpdate(controlled);
    controlled = true;
    if (reloading || !update) return;
    if (mayReloadNow(window.location.pathname)) {
      reloading = true;
      window.location.reload();
      return;
    }
    pendingReload = true;
  });
}

function supported(): boolean {
  return !import.meta.env.DEV && typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

function register(): void {
  void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((err: unknown) => {
    // The app works without it; it only loses offline start-up.
    console.warn('Service worker registration failed', err);
  });
}

/** Register on the next idle moment. Safe to call many times. */
export function registerServiceWorkerSoon(): void {
  if (requested) return;
  if (!supported()) return;
  watchForNewBuild();
  requested = true;
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(register, { timeout: 5_000 });
  } else {
    window.setTimeout(register, 1_000);
  }
}

/** The backstop: ten seconds after load, whatever the screen did. */
export function registerServiceWorkerAfterLoad(): void {
  if (typeof window === 'undefined' || !supported()) return;
  watchForNewBuild();
  const later = () => window.setTimeout(registerServiceWorkerSoon, 10_000);
  if (document.readyState === 'complete') later();
  else window.addEventListener('load', later, { once: true });
}
